import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { useToast } from '../ui/toast.jsx';
import { ConfirmDialog } from '../ui/confirm-dialog.jsx';
import { NodeTable } from '../nodes/node-table.jsx';
import { NodeFormModal } from '../nodes/node-form-modal.jsx';
import { IssueCertModal } from '../certificates/issue-modal.jsx';
import { IssuedBundleModal } from '../certificates/issued-bundle-modal.jsx';
import { BootstrapTokensModal } from '../nodes/bootstrap-tokens-modal.jsx';
import { api, ApiError } from '../../api/index.js';

// «All» tab is always first; the rest of the tabs come from the server's
// operator-managed tier catalogue (Settings → Nodes → Node tiers, settings
// key `node_tiers`). Backend ships the live list as `tiers` in the same
// /api/nodes response that powers the table — same round-trip, no race.
//
// Icon mapping: a small static map covers the canonical tiers (HQ/C4ISR/
// C2/C1); anything custom the operator adds falls back to a generic
// server glyph so the UI stays readable without an icon-picker UI on the
// settings page.
const TIER_ICON_MAP = {
  HQ:    'fa-flag',
  C4ISR: 'fa-tower-cell',
  C2:    'fa-network-wired',
  C1:    'fa-server',
};
const FALLBACK_TIER_ICON = 'fa-server';

function buildTierTabs(tiers) {
  const tabs = [{ id: 'all', label: 'All', title: '', icon: 'fa-list' }];
  if (!Array.isArray(tiers)) return tabs;
  for (const t of tiers) {
    if (!t || !t.value) continue;
    tabs.push({
      id: t.value,
      // Tab label = the short tier CODE (`t.value`, e.g. "C4ISR") so the
      // filter bar stays compact. The long human-readable form
      // (`t.label`, e.g. "C4ISR - full command") goes into `title` and
      // surfaces as the browser tooltip on hover — useful when an
      // operator forgot what a code means but distracting if shown
      // inline (early 2026-05-25 rev briefly showed `t.label` inline →
      // tabs became multi-line on small viewports, operator flagged
      // immediately).
      label: t.value,
      title: t.label || '',
      icon: TIER_ICON_MAP[t.value] || FALLBACK_TIER_ICON,
    });
  }
  return tabs;
}

export function NodesPage({ currentUser }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [tierFilter, setTierFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput), 250);
    return () => clearTimeout(id);
  }, [searchInput]);

  const [formState, setFormState] = useState({ open: false, mode: 'create', initial: null });
  const [deleteState, setDeleteState] = useState({ open: false, node: null });
  const [issueState, setIssueState] = useState({ open: false, mode: 'issue-for-node', node: null, certificate: null });
  const [bundleState, setBundleState] = useState({ open: false, cert: null, bundle: null });
  const [bootstrapState, setBootstrapState] = useState({ open: false, node: null });

  const isAdmin = currentUser?.role === 'admin';

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.nodes.list({
        tier: tierFilter === 'all' ? '' : tierFilter,
        q: search,
      });
      setData(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message);
    } finally {
      setLoading(false);
    }
  }, [tierFilter, search]);

  useEffect(() => { refresh(); }, [refresh]);

  async function handleSave(payload) {
    if (formState.mode === 'create') {
      await api.nodes.create(payload);
      toast.success(`Node "${payload.name}" created.`);
    } else {
      await api.nodes.update(formState.initial.id, payload);
      toast.success('Node updated.');
    }
    setFormState({ open: false, mode: 'create', initial: null });
    await refresh();
  }

  async function handleDelete() {
    try {
      await api.nodes.remove(deleteState.node.id);
      toast.success(`Node "${deleteState.node.name}" deleted.`);
      setDeleteState({ open: false, node: null });
      await refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Delete failed.');
    }
  }

  function handleIssueCert(node) {
    const activeCertId = node.certificates?.active?.id;
    if (activeCertId) {
      // Renew: load the active cert and reuse the renew flow.
      api.certificates
        .get(activeCertId)
        .then((r) =>
          setIssueState({ open: true, mode: 'renew', node, certificate: r.certificate }),
        )
        .catch((err) =>
          toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Failed to load active cert.'),
        );
    } else {
      setIssueState({ open: true, mode: 'issue-for-node', node, certificate: null });
    }
  }

  async function handleIssueSubmit(payload) {
    if (issueState.mode === 'renew' && issueState.certificate) {
      const res = await api.certificates.renew(issueState.certificate.id, payload);
      setIssueState({ open: false, mode: 'issue-for-node', node: null, certificate: null });
      setBundleState({ open: true, cert: res.certificate, bundle: res.bundle });
      toast.success(`Certificate "${res.certificate.subjectCn}" renewed.`);
    } else {
      const res = await api.certificates.issue(payload);
      setIssueState({ open: false, mode: 'issue-for-node', node: null, certificate: null });
      setBundleState({ open: true, cert: res.certificate, bundle: res.bundle });
      toast.success(`Certificate "${res.certificate.subjectCn}" issued.`);
    }
    await refresh();
  }

  const stats = data?.stats || { total: 0, active: 0, planned: 0, suspended: 0, decommissioned: 0 };

  // Derive Tier-filter tabs from the live server response. Until the first
  // load finishes (data === null) `buildTierTabs(undefined)` returns just
  // the «All» pill — keeps the bar visible during the initial fetch.
  const tierTabs = useMemo(() => buildTierTabs(data?.tiers), [data?.tiers]);

  // If the operator removes a tier on Settings → Nodes that we currently
  // have selected as the filter, snap back to «All» so the table doesn't
  // forever filter on a tier that no longer exists. tierFilter is the
  // local state; tierTabs is the live truth.
  useEffect(() => {
    if (tierFilter === 'all') return;
    const known = tierTabs.some((t) => t.id === tierFilter);
    if (!known) setTierFilter('all');
  }, [tierFilter, tierTabs]);

  return (
    <PageShell
      icon="fa-network-wired"
      title="Nodes"
      subtitle="Mesh node registry — middle-tier TMS-Mesh-microservice instances"
      actions={
        isAdmin && (
          <button
            type="button"
            onClick={() => setFormState({ open: true, mode: 'create', initial: null })}
            className="btn-primary"
          >
            <i className="fa-solid fa-plus" aria-hidden="true" />
            Create node
          </button>
        )
      }
    >
      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <StatCard icon="fa-list"           label="Total"          value={stats.total} />
        <StatCard icon="fa-circle-check"   label="Active"         value={stats.active}        tone="success" />
        <StatCard icon="fa-clock"          label="Planned"        value={stats.planned}       tone="mist" />
        <StatCard icon="fa-pause"          label="Suspended"      value={stats.suspended}     tone="warning" />
        <StatCard icon="fa-power-off"      label="Decommissioned" value={stats.decommissioned} tone="rose" />
      </div>

      {/* Filters */}
      <div className="card mb-4 flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1 rounded-2xl border border-line bg-white p-1">
          {tierTabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTierFilter(t.id)}
              title={t.title || undefined}
              className={
                tierFilter === t.id
                  ? 'flex items-center gap-2 rounded-xl bg-accent-soft px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-accent'
                  : 'flex items-center gap-2 rounded-xl px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-mist hover:text-ink'
              }
            >
              <i className={`fa-solid ${t.icon} text-[11px]`} aria-hidden="true" />
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:max-w-xs">
          <i className="fa-solid fa-magnifying-glass pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-mist" aria-hidden="true" />
          <input
            type="search"
            className="field pl-10"
            placeholder="Search by name, URL, description…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div role="alert" className="card mb-4 border-rose-200 bg-rose-soft px-5 py-4 text-sm text-rose-700">
          <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="card px-6 py-16 text-center text-mist">
          <span className="loader-spinner mx-auto mb-3 text-accent" aria-hidden="true" />
          <div className="text-sm">Loading nodes…</div>
        </div>
      ) : (
        <NodeTable
          rows={data?.nodes || []}
          viewerTimezone={currentUser?.timezone || 'UTC'}
          onEdit={(n) => setFormState({ open: true, mode: 'edit', initial: n })}
          onIssueCert={handleIssueCert}
          onIssueBootstrap={(n) => setBootstrapState({ open: true, node: n })}
          onDelete={(n) => setDeleteState({ open: true, node: n })}
        />
      )}

      <NodeFormModal
        open={formState.open}
        mode={formState.mode}
        initial={formState.initial}
        onClose={() => setFormState({ open: false, mode: 'create', initial: null })}
        onSave={handleSave}
      />

      <IssueCertModal
        open={issueState.open}
        mode={issueState.mode}
        node={issueState.node}
        certificate={issueState.certificate}
        onClose={() => setIssueState({ open: false, mode: 'issue-for-node', node: null, certificate: null })}
        onSubmit={handleIssueSubmit}
      />

      <IssuedBundleModal
        open={bundleState.open}
        certificate={bundleState.cert}
        bundle={bundleState.bundle}
        onClose={() => setBundleState({ open: false, cert: null, bundle: null })}
      />

      <ConfirmDialog
        open={deleteState.open}
        title={`Delete ${deleteState.node?.name || 'node'}?`}
        message="This removes the node from the registry. Historical certificates remain in the database but are no longer linked to a node. This action cannot be undone."
        confirmLabel="Delete"
        destructive
        onClose={() => setDeleteState({ open: false, node: null })}
        onConfirm={handleDelete}
      />

      <BootstrapTokensModal
        open={bootstrapState.open}
        node={bootstrapState.node}
        viewerTimezone={currentUser?.timezone || 'UTC'}
        onClose={() => setBootstrapState({ open: false, node: null })}
      />
    </PageShell>
  );
}

function StatCard({ icon, label, value, tone = 'accent' }) {
  const tones = {
    accent:  'bg-accent-soft text-accent',
    success: 'bg-success-soft text-success',
    warning: 'bg-warning-soft text-warning',
    rose:    'bg-rose-soft text-rose-700',
    mist:    'bg-canvas text-mist',
  };
  return (
    <div className="card flex items-center justify-between px-5 py-4">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-mist">{label}</div>
        <div className="mt-0.5 text-2xl font-semibold tracking-tight text-ink">{value}</div>
      </div>
      <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tones[tone]}`}>
        <i className={`fa-solid ${icon} text-lg`} aria-hidden="true" />
      </div>
    </div>
  );
}
